/** Typed report-boundary readers for historical provider payloads.
 * No storage migration: a no-result code is never a distance observation.
 */
export interface NrcProximityInput {
  nearestReactorName?: string | null;
  nearestReactorDistKm?: number | null;
  reactorsWithin200km?: number | null;
}

export interface NrcProximityEvidence {
  status: 'catalog-distance' | 'none-in-radius' | 'unresolved';
  name: string | null;
  distanceKm: number | null;
  summary: string;
}

export function resolveNrcProximity(data: NrcProximityInput): NrcProximityEvidence {
  const name = data.nearestReactorName?.trim();
  const noResultName = !!name && /^(?:none|no reactors?)(?:\b|$)/i.test(name);
  if (data.reactorsWithin200km === 0 || noResultName) {
    return { status: 'none-in-radius', name: null, distanceKm: null,
      summary: 'No reactor site identified within the searched 200 km static-catalog radius; nearest distance outside that radius unresolved.' };
  }
  const distanceKm = data.nearestReactorDistKm;
  // The provider only searches 200 km. 999 is its legacy no-result sentinel;
  // any out-of-radius/non-finite value is not a nearest-distance observation.
  if ((name != null && /^(?:unknown|n\/a)$/i.test(name))
    || typeof distanceKm !== 'number' || !Number.isFinite(distanceKm)
    || distanceKm < 0 || distanceKm > 200) {
    return { status: 'unresolved', name: null, distanceKm: null,
      summary: 'Nearest catalog reactor distance unresolved; no valid within-radius distance observation is available.' };
  }
  return { status: 'catalog-distance', name: name || null, distanceKm,
    summary: `Nearest recorded catalog reactor site: ${name || 'name not recorded'} (${distanceKm} km straight-line distance).` };
}

export interface ConnectivityProximityInput {
  nearestIxName?: string | null;
  nearestIxDistKm?: number | null;
  nearestIxKind?: 'ixp' | 'colo-fallback';
  ixCount50km?: number | null;
}

/** Preserve the returned entity kind; a failed IX inventory is not zero IXs. */
export function connectivityProximityPresentation(data: ConnectivityProximityInput) {
  const isColocationFallback = data.nearestIxKind === 'colo-fallback';
  const distance = typeof data.nearestIxDistKm === 'number'
    && Number.isFinite(data.nearestIxDistKm) && data.nearestIxDistKm >= 0
    ? `${data.nearestIxDistKm} km` : 'Unknown';
  const count = typeof data.ixCount50km === 'number' && Number.isInteger(data.ixCount50km)
    && data.ixCount50km >= 0 ? String(data.ixCount50km) : 'Unresolved';
  return {
    nameLabel: isColocationFallback ? 'Nearest colocation facility' : 'Nearest IX',
    name: data.nearestIxName?.trim() || 'Unknown',
    distance,
    meta: `${distance} to ${isColocationFallback ? 'colocation fallback' : 'IX'} (straight-line)`,
    ixInventory: isColocationFallback ? 'Unresolved (colocation fallback)' : count,
    limitation: isColocationFallback
      ? 'IX inventory unresolved; this colocation fallback does not establish an IX, fiber route, carrier service or measured latency.'
      : 'Recorded IX inventory only; geographic proximity does not establish a fiber route, carrier service or measured latency.',
  };
}
